// #351 slice A2, brief §5.3-5.4: what a rule sheet tells before it writes. A
// row that loses its test or deload effect, and a threshold that never
// raises the load in the program's rounds. Warn, never block.
// Keep free of React and any UI import.
import {
  generateRounds,
  type GenerateOptions,
  type ProgressionRule,
} from "./programProgression.js";
import type { RoundType } from "./schemas/programV3.js";

const hasTestEffect = (r: ProgressionRule): boolean => r.test?.amrapLast === true || !!r.test?.delta;
const hasDeloadEffect = (r: ProgressionRule): boolean =>
  r.deload?.factor !== undefined || !!r.deload?.delta || !!r.deload?.setsDelta;

/** What `next` leaves out that `prev` had, in the round types the program
 *  has: «no AMRAP in the test round», «no deload». Nothing for a program with
 *  no such round. */
export const lostEffects = (
  prev: ProgressionRule,
  next: ProgressionRule,
  types: readonly RoundType[],
): { test: boolean; deload: boolean } => ({
  test: types.includes("test") && hasTestEffect(prev) && !hasTestEffect(next),
  deload: types.includes("deload") && hasDeloadEffect(prev) && !hasDeloadEffect(next),
});

/** How far past the program the first rise is looked for. */
const HORIZON = 200;

/** A threshold rule whose load does not rise in the program's `rounds`: the
 *  round (0-based) where the first rise would come, null when it comes in no
 *  reasonable time or the step is 0. undefined: the load does rise (or the
 *  rule is no threshold). */
export const thresholdStall = (
  rule: ProgressionRule,
  types: readonly RoundType[],
  rounds: number,
  opts: GenerateOptions = {},
): { firstRise: number | null } | undefined => {
  const thr = rule.threshold;
  if (!thr || thr.loadStep <= 0 || rounds < 1) return undefined;
  const gen = generateRounds(rule, types, rounds + HORIZON, opts);
  const load = (w: number): number | null => gen[w]?.[0]?.value ?? null;
  const start = load(0);
  if (start === null) return undefined;
  const rose = (w: number) => (types[w] ?? "normal") !== "deload" && (load(w) ?? start) > start;
  for (let w = 1; w < rounds; w += 1) if (rose(w)) return undefined;
  for (let w = rounds; w < rounds + HORIZON; w += 1) if (rose(w)) return { firstRise: w };
  return { firstRise: null };
};
